import { Capacitor } from '@capacitor/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { OFFLINE_CACHE_HEADER, type HttpPort, type HttpRequest, type HttpResponse } from '../chat/core/ports';
import { isNative } from './platform';

/**
 * Last-known copies of slow-changing server responses, so a cold start with no network
 * still reaches the app. A copy is served only when a request gets no response at all;
 * any HTTP status, 401 included, goes through unchanged. Live data never enters the
 * cache: only the paths below are stored. A served copy carries the header
 * `X-Maper-Offline-Cache: 1` (`OFFLINE_CACHE_HEADER`).
 *
 * Files live in Directory.Data, which the OS removes only on uninstall. The full tree of
 * a Maper employee is about 3.5 MB, which rules out Preferences and localStorage.
 */

const DIR = 'offline-cache';
const PROFILE_KEY = 'auth-profile';

// Matched against the full request URL, query included.
const CACHEABLE = [
    /\/api\/translations\/(\?.*)?$/,
    /\/api\/model\/v3\/measurements\/global_metadata\/$/,
    /\/api\/model\/node\/schema\/$/,
    /\/api\/model\/tree\/\?max_depth=MACHINE$/,
    /\/api\/login\/user\/$/,
    /\/api\/tag\/node_tags\/\?/,
    // A node's media list, so a node image in the device media cache is found after a restart
    // offline: the list holds the URL whose path is the image's cache key.
    /\/api\/v2\/media\/\?node_id=\d+$/,
    /\/api\/chat\/conversations\/(\?.*)?$/,
    // History the chat view needs to render: the newest page of a node's feed, and older
    // message pages, which never change once written.
    /\/api\/feed\/\?(?!.*cursor=).*node_id=/,
    /\/api\/chat\/conversations\/\d+\/messages\/\?before_seq=/,
];

// The thread asks for a different page size on each open, so an older-messages page is
// stored under its URL without the limit.
const keyOf = (url: string): string => (/\/messages\/\?before_seq=/.test(url) ? url.replace(/&limit=\d+/, '') : url);

const isCacheable = (method: string | undefined, url: string): boolean =>
    (method ?? 'get').toLowerCase() === 'get' && CACHEABLE.some((pattern) => pattern.test(url));

// FNV-1a, for file names and for change detection.
const fnv = (text: string): string => {
    let hash = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16);
};

const fileFor = (key: string): string => `${DIR}/${fnv(keyOf(key))}.json`;
const hashFileFor = (key: string): string => `${DIR}/${fnv(keyOf(key))}.hash`;

const write = (path: string, data: string) =>
    Filesystem.writeFile({ path, data, directory: Directory.Data, encoding: Encoding.UTF8, recursive: true });

// Hash of the content last written per key. Seeded from the small .hash file on the first save of
// a session, so an unchanged response never sends the large file across the bridge again.
const writtenHash = new Map<string, string | undefined>();

const readWrittenHash = async (key: string): Promise<string | undefined> => {
    try {
        const { data } = await Filesystem.readFile({
            path: hashFileFor(key),
            directory: Directory.Data,
            encoding: Encoding.UTF8,
        });
        return data as string;
    } catch {
        return undefined;
    }
};

const since = (start: number): number => Math.round(performance.now() - start);

// The body is stored as the server sent it, so neither a save nor a serve parses it.
const save = async (key: string, body: string): Promise<void> => {
    const started = performance.now();
    const hash = fnv(body);
    const prepared = performance.now();
    key = keyOf(key);
    if (!writtenHash.has(key)) writtenHash.set(key, await readWrittenHash(key));
    const previous = writtenHash.get(key);
    if (previous === hash) {
        console.info(`[offlineCache] unchanged ${key} (prep ${since(started)} ms)`);
        return;
    }
    // Claimed before the write, so a concurrent save of the same content skips.
    writtenHash.set(key, hash);
    try {
        await write(fileFor(key), `{"key":${JSON.stringify(key)},"savedAt":${Date.now()},"data":${body}}`);
        await write(hashFileFor(key), hash);
    } catch (error) {
        writtenHash.set(key, previous);
        throw error;
    }
    console.info(
        `[offlineCache] saved ${key} (${body.length} chars, prep ${Math.round(prepared - started)} ms, ` +
            `write ${since(prepared)} ms)`,
    );
};

const DATA_FIELD = ',"data":';

// Reads go through the WebView's local file server: a plain fetch streams the file, where
// Filesystem.readFile packs it into one JSON bridge message. Only the small header is parsed;
// the body comes back as text.
const load = async (key: string): Promise<string | undefined> => {
    const started = performance.now();
    try {
        const { uri } = await Filesystem.getUri({ path: fileFor(key), directory: Directory.Data });
        const response = await fetch(Capacitor.convertFileSrc(uri));
        if (!response.ok) return undefined;
        const text = await response.text();
        // A JSON string escapes its quotes, so the key cannot contain the field marker.
        const split = text.indexOf(DATA_FIELD);
        const header = JSON.parse(`${text.slice(0, split)}}`);
        if (header.key !== keyOf(key)) return undefined;
        console.info(
            `[offlineCache] served ${key} (${text.length} chars, read ${since(started)} ms, ` +
                `t=${Math.round(performance.now())}, saved ${new Date(header.savedAt).toISOString()})`,
        );
        return text.slice(split + DATA_FIELD.length, -1);
    } catch {
        return undefined;
    }
};

const report = (what: string) => (error: unknown) => console.error(`[offlineCache] ${what} failed:`, error);

export const clearOfflineCache = async (): Promise<void> => {
    if (!isNative()) return;
    writtenHash.clear();
    await Filesystem.rmdir({ path: DIR, directory: Directory.Data, recursive: true }).catch(() => undefined);
    console.info('[offlineCache] cleared');
};

export const saveProfile = (profile: unknown): void => {
    if (isNative()) save(PROFILE_KEY, JSON.stringify(profile)).catch(report('profile save'));
};

export const loadProfile = async (): Promise<Record<string, unknown> | undefined> => {
    if (!isNative()) return undefined;
    const text = await load(PROFILE_KEY);
    return text === undefined ? undefined : (JSON.parse(text) as Record<string, unknown>);
};

/** Feeds a fresh copy into the store the way the normal load does. Gets the parsed body and the URL. */
type FreshHandler = (data: unknown, url: string) => void;
const freshHandlers: Array<[RegExp, FreshHandler]> = [];

export const onFreshResponse = (pattern: RegExp, handler: FreshHandler): void => {
    freshHandlers.push([pattern, handler]);
};

const revalidated = new Set<string>();

const revalidate = async (inner: HttpPort, request: HttpRequest, handler: FreshHandler): Promise<void> => {
    // The caller already has its answer, so its abort signal no longer applies.
    const response = await inner.fetch({ ...request, signal: undefined });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const text = await response.text();
    handler(JSON.parse(text), request.url);
    console.info(`[offlineCache] fresh ${request.url} t=${Math.round(performance.now())}`);
    await save(request.url, text);
};

// A copy served from disk says so, for the callers that treat it as older than any live answer.
const diskHeaders: HttpResponse['headers'] = {
    get: (name) => {
        const key = name.toLowerCase();
        if (key === 'content-type') return 'application/json';
        return key === OFFLINE_CACHE_HEADER.toLowerCase() ? '1' : null;
    },
};

const textResponse = (text: string, headers: HttpResponse['headers']): HttpResponse => ({
    status: 200,
    ok: true,
    headers,
    json: async () => JSON.parse(text),
    text: async () => text,
    arrayBuffer: async () => new TextEncoder().encode(text).buffer as ArrayBuffer,
});

/**
 * The offline cache in front of the app's transport. An allowlisted GET is saved on success and
 * served from the device when the request gets no response. A URL with a fresh handler is served
 * from the device first, once per session, and refreshed in the background; the handler feeds the
 * fresh copy into the store the same way the normal load does.
 */
export const withOfflineCache = (inner: HttpPort): HttpPort => {
    if (!isNative()) return inner;
    return {
        async fetch(request: HttpRequest): Promise<HttpResponse> {
            const { url } = request;
            if (!isCacheable(request.method, url)) return inner.fetch(request);
            const handler = freshHandlers.find(([pattern]) => pattern.test(url))?.[1];
            if (handler && !revalidated.has(url)) {
                revalidated.add(url);
                const saved = await load(url);
                if (saved !== undefined) {
                    revalidate(inner, request, handler).catch(report(`revalidate ${url}`));
                    return textResponse(saved, diskHeaders);
                }
            }
            let response: HttpResponse;
            try {
                response = await inner.fetch(request);
            } catch (error) {
                const saved = await load(url);
                if (saved === undefined) throw error;
                return textResponse(saved, diskHeaders);
            }
            if (!response.ok) return response;
            const text = await response.text();
            save(url, text).catch(report(`save ${url}`));
            return textResponse(text, response.headers);
        },
    };
};
